import { useState } from 'react';

import { Card, CardContent, Typography, Grid, Box } from '@mui/material';

import { useAppContext } from '@/context';
import TAGButton from '@/components/TAGButton';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import { convertNameToUrlFormat } from '@/utils/convertNameToUrlFormat/convertNameToUrlFormat';
import TAGDialog from '@/components/TAGDialog';
import Drawer from '@/components/Drawer/Drawer';

import { TAGDialogStyles } from '../Staff/List/Staff.styles';

import {
	getCardStyles,
	getCardContentStyles,
	getJobTitleStyles,
	getCollegeNameStyles,
	getProviderButtonStyles,
	getProviderPopLinkStyles,
} from './StaffCard.styles';
import StaffImage from './StaffImage';

export default function StaffCard({
	firstName,
	lastName,
	jobTitle,
	collegeName,
	isSmallScreen,
	photoUrl,
	npi,
	biography,
	name,
}: {
	firstName: string;
	lastName: string;
	jobTitle?: string | null;
	collegeName?: string | null;
	isSmallScreen: boolean;
	photoUrl?: string | null;
	npi?: string | null;
	biography?: string | null;
	name?: string | null;
}) {
	const { config } = useAppContext();
	const [popDisplay, setPopDisplay] = useState(false);
	const nameForUrl = convertNameToUrlFormat(`${firstName} ${lastName}`);
	const providerUrl = npi ? `${config.features.providers.root}${nameForUrl}/${npi}/` : '';
	const showProviderPageCTA = config.featureFlags?.showProviderPageCtaInMeetOurStaff;
	const showReadMoreForBio = config.featureFlags?.showReadMoreForBioInMeetOurStaff;
	const displayName = name ? `${name}` : `${firstName} ${lastName}`;

	const StaffFullBio = () => {
		if (!biography) return null;
		return (
			<Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2 }}>
				<Grid item xs={3}>
					<Box position="relative">
						<StaffImage photoUrl={photoUrl} firstName={firstName} />
					</Box>
				</Grid>
				<Grid item xs={9}>
					<Typography
						variant="header3"
						color="text.primary"
						marginBottom={1}
						data-test-id={`staff_card_bio_name`}
					>
						{displayName}
					</Typography>
					<Typography color="text.secondary" data-test-id={`staff_card_bio_copy`}>
						{biography}
					</Typography>
				</Grid>
			</Grid>
		);
	};

	return (
		<Card sx={getCardStyles}>
			<StaffImage photoUrl={photoUrl} firstName={firstName} />
			<CardContent sx={getCardContentStyles}>
				<Typography variant="header3" color="text.primary" data-test-id={`staff_card_${firstName}_${lastName}`}>
					{displayName}
				</Typography>
				<Typography
					variant={isSmallScreen ? 'bodyMediumBold' : 'bodyLargeBold'}
					color="text.secondary"
					sx={getJobTitleStyles}
					data-test-id={`staff_card_${jobTitle || ''}`}
				>
					{jobTitle}
				</Typography>
				{collegeName ? (
					<Typography
						variant={isSmallScreen ? 'bodyMediumBook' : 'bodyLargeBook'}
						color="text.secondary"
						sx={getCollegeNameStyles}
					>
						{collegeName}
					</Typography>
				) : null}
				{biography && showReadMoreForBio && (
					<Box data-test-id={`staff_card_bio-short`}>
						<Typography color="text.secondary">{`${biography.slice(0, 130)}...`}</Typography>
						<TAGButton
							dataTestId={`staff_card_bio-popup-trigger`}
							size="M"
							variant="tertiaryDefault"
							sx={getProviderPopLinkStyles}
							onClick={() => setPopDisplay(true)}
						>
							Read More <TAGSvgIcon icon="ArrowRightFunc" size={24} />
						</TAGButton>

						{/*  Dialog or Drawer based on viewport */}
						{isSmallScreen ? (
							<Drawer anchor="top" open={popDisplay} onClose={() => setPopDisplay(false)} closeButton>
								<StaffFullBio />
							</Drawer>
						) : (
							<TAGDialog
								open={popDisplay}
								handleClose={() => setPopDisplay(false)}
								dialogSx={TAGDialogStyles}
								renderTextContent={() => <StaffFullBio />}
								renderActions={() => (
									<TAGButton variant="tertiaryDefault" onClick={() => setPopDisplay(false)} size="M">
										Dismiss
									</TAGButton>
								)}
							/>
						)}
					</Box>
				)}
				{npi && showProviderPageCTA && (
					<TAGButton
						size="M"
						variant="secondaryDefault"
						dataTestId={`staff_card_more_${firstName}`}
						href={providerUrl}
						sx={getProviderButtonStyles}
					>
						More about {name || firstName}
					</TAGButton>
				)}
			</CardContent>
		</Card>
	);
}
